"use client";

import * as React from "react";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";

import { cn } from "@/lib/utils";
import { isNativePlatform, presentDatePicker } from "@/hooks/use-datetime-picker";

interface DatePickerProps {
  date: Date | undefined;
  onSelect: (date: Date | undefined) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  min?: Date;
  max?: Date;
}

export function DatePicker({
  date,
  onSelect,
  required,
  disabled,
  className,
  min,
  max,
}: DatePickerProps) {
  const native = isNativePlatform();

  const handleNativeClick = async () => {
    if (disabled) return;

    const result = await presentDatePicker({
      value: date,
      min,
      max,
    });

    if (result) {
      onSelect(result);
    }
  };

  // Format date as YYYY-MM-DD for native input
  const inputValue = date ? format(date, "yyyy-MM-dd") : "";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      // Parse as local date (not UTC)
      const [year, month, day] = value.split("-").map(Number);
      onSelect(new Date(year, month - 1, day));
    } else {
      onSelect(undefined);
    }
  };

  const containerStyles = cn(
    "relative w-full",
    native && "cursor-pointer",
    native && disabled && "cursor-not-allowed opacity-50"
  );

  const inputStyles = cn(
    // Reset iOS native styling
    "appearance-none [-webkit-appearance:none] [-moz-appearance:none]",
    // Base styles matching Input component exactly
    "flex h-9 w-full min-w-0 box-border rounded-md border border-input bg-transparent",
    "px-3 py-1 pr-10 shadow-sm transition-colors",
    "!text-base md:!text-sm leading-normal",
    "placeholder:text-muted-foreground",
    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
    "disabled:cursor-not-allowed disabled:opacity-50",
    // Hide native picker icon on web
    !native && "[&::-webkit-calendar-picker-indicator]:opacity-0",
    !native && "[&::-webkit-calendar-picker-indicator]:absolute",
    !native && "[&::-webkit-calendar-picker-indicator]:inset-0",
    !native && "[&::-webkit-calendar-picker-indicator]:w-full",
    !native && "[&::-webkit-calendar-picker-indicator]:h-full",
    !native && "[&::-webkit-calendar-picker-indicator]:cursor-pointer",
    // iOS specific - text alignment
    "[&::-webkit-date-and-time-value]:text-left",
    "[&::-webkit-datetime-edit]:p-0",
    "[&::-webkit-datetime-edit-fields-wrapper]:p-0",
    // Firefox
    !native && "[&::-moz-calendar-picker-indicator]:opacity-0",
    className
  );

  if (native) {
    // Native: Display-only div that triggers native picker on click
    return (
      <div className={containerStyles} onClick={handleNativeClick}>
        <div className={inputStyles}>
          {date ? (
            format(date, "MMM d, yyyy")
          ) : (
            <span className="text-muted-foreground">Select date</span>
          )}
        </div>
        <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      </div>
    );
  }

  // Web: Use native HTML input
  return (
    <div className={containerStyles}>
      <input
        type="date"
        value={inputValue}
        onChange={handleChange}
        disabled={disabled}
        required={required}
        min={min ? format(min, "yyyy-MM-dd") : undefined}
        max={max ? format(max, "yyyy-MM-dd") : undefined}
        className={inputStyles}
      />
      <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
    </div>
  );
}
